import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import ProductCard from "@/components/base/ProductCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { products } from "@/mocks/products";

const CATS = ["All", "Earrings", "Necklaces", "Rings", "Bracelets"] as const;
const SORTS = ["Featured", "Price: Low to High", "Price: High to Low", "Newest"] as const;

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("category") || "All";
  const [cat, setCat] = useState<string>(initial);
  const [sort, setSort] = useState<string>("Featured");

  useEffect(() => {
    setCat(params.get("category") || "All");
  }, [params]);

  const items = useMemo(() => {
    let list = cat === "All" ? [...products] : products.filter((p) => p.category === cat);
    if (sort === "Price: Low to High") list.sort((a, b) => a.price - b.price);
    if (sort === "Price: High to Low") list.sort((a, b) => b.price - a.price);
    return list;
  }, [cat, sort]);

  const setCategory = (c: string) => {
    setCat(c);
    if (c === "All") params.delete("category");
    else params.set("category", c);
    setParams(params, { replace: true });
  };

  return (
    <Layout>
      <section>
        <div className="shell pt-4 md:pt-24 pb-3 md:pb-12">
          <Breadcrumb items={cat === "All" ? [{ label: "Shop" }] : [{ label: "Shop", to: "/shop" }, { label: cat }]} />
          <h1 className="mt-2 md:mt-8 font-display text-2xl md:text-6xl text-black">All Jewelry</h1>
          <p className="hidden md:block mt-6 text-[15px] text-graphite max-w-xl leading-relaxed">
            Considered pieces cast in recycled precious metals. Every object is hand-finished in our Antwerp atelier.
          </p>
        </div>

        <div className="shell border-t border-b border-stone">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 py-2 md:py-4">
            <div className="flex flex-nowrap md:flex-wrap items-center gap-1 -mx-1 px-1 overflow-x-auto md:overflow-visible">
              {CATS.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`shrink-0 h-9 md:h-11 px-3 md:px-4 text-[11px] md:text-[12px] tracking-[0.14em] md:tracking-[0.16em] uppercase whitespace-nowrap cursor-pointer border transition-colors ${
                    cat === c ? "border-black text-black" : "border-transparent text-graphite hover:text-black"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="hidden md:flex items-center gap-3">
              <label className="eyebrow">Sort</label>
              <span className="select-wrap">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="field"
                >
                  {SORTS.map((s) => <option key={s}>{s}</option>)}
                </select>
                <i className="ri-arrow-down-s-line select-chevron"></i>
              </span>
            </div>
          </div>
        </div>

        <div className="shell py-8 md:py-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 md:gap-x-6 gap-y-8 md:gap-y-16" data-product-shop>
            {items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
          {items.length === 0 && (
            <p className="text-center text-graphite py-24">No pieces found in this category.</p>
          )}
        </div>
      </section>
    </Layout>
  );
}